import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Input.module.css';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  /** Muted unit / currency tail (e.g. `Dhs · HT`, `%`). */
  suffix?: ReactNode;
  /**
   * When false and `error` is set, the control keeps error border / `aria-invalid` but the message
   * is not rendered here (caller shows it elsewhere).
   */
  showErrorText?: boolean;
};

export function Input({
  label,
  hint,
  error,
  className,
  id,
  suffix,
  showErrorText = true,
  required,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? rest.name ?? autoId;
  const errorId = `${inputId}-error`;
  const hasSuffix = !!suffix;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const inputEl = (
    <input
      id={inputId}
      className={cx(
        styles.input,
        hasSuffix ? styles.inputAffix : undefined,
        !hasSuffix && error ? styles.error : undefined,
      )}
      aria-invalid={!!error}
      aria-required={required || undefined}
      aria-describedby={describedBy}
      required={required}
      {...rest}
    />
  );
  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {suffix ? (
        <div
          className={cx(
            styles.affixWrap,
            error ? styles.affixError : undefined,
            rest.disabled ? styles.affixDisabled : undefined,
          )}
        >
          {inputEl}
          <span className={styles.suffix}>{suffix}</span>
        </div>
      ) : (
        inputEl
      )}
      {showErrorText && error ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
